import { capitalize } from "lodash";
import { memo, ReactNode, useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import Select, {
  components,
  CSSObjectWithLabel,
  OptionProps,
  SingleValueProps
} from "react-select";

import { ClassificationIcon, Icon, RenderWhen } from "../../../../components";
import { MenuItem } from "../../../../components/@types";
import { formatCount } from "../../../../components/PageResultsCount";
import usePagination from "../../../../hooks/usePagination";
import { CustomDropdownIndicator } from "../../../search/components/quicksearchtabs/files/components/CustomDropdownIndicator";
import { CustomInput } from "../../../search/components/quicksearchtabs/files/components/CustomInput";
import { CustomMultiValueContainer } from "../../../search/components/quicksearchtabs/files/components/CustomMultiValueContainer";
import { CustomNoOptionsMessage } from "../../../search/components/quicksearchtabs/files/components/CustomNoOptionMessage";
import { CustomOption } from "../../../search/components/quicksearchtabs/files/components/CustomOption";
import { CLASSIFICATION_KEYWORDS } from "../../../search/consts";
import { useLocalSampleSummaryParams } from "../../hooks";
import { NetworkRepWithSource } from "../report_summary/@types";
import NetworkReferencesGrid from "../report_summary/cards/network_references/NetworkReferencesGrid";
import { ANALYSIS } from "../report_summary/consts";
import useNetworkReferences from "../report_summary/hooks/useNetworkReferences";

interface OptionType {
  value: string;
  label: string;
  icon: ReactNode;
}

const selectContainerStyle = (base: CSSObjectWithLabel) => ({
  ...base,
  width: "200px"
});

const customSingleValue = (props: SingleValueProps<OptionType, false>) => (
  <components.SingleValue {...props}>
    <div
      style={{ display: "flex", alignItems: "center" }}
      data-testid={`network_refs_classification_${props.data.value}`}
    >
      {props.data.icon}
      <span style={{ marginLeft: 5 }}>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const customOption = (props: OptionProps<OptionType, false>) => (
  <components.Option {...props}>
    <div
      style={{ display: "flex", alignItems: "center" }}
      data-testid={`network_refs_classification_${props.data.value}`}
    >
      {props.data.icon}
      <span style={{ marginLeft: 5 }}>{props.data.label}</span>
    </div>
  </components.Option>
);
const filterByValue = (item: NetworkRepWithSource, value: string) =>
  value ? item.requested_network_location.toLowerCase().includes(value) : true;

const filterByClassification = (
  item: NetworkRepWithSource,
  classification: string
) => (classification ? item.classification === classification : true);

const filterBySource = (item: NetworkRepWithSource, source: string) =>
  source ? item.extra.map(src => src.source).includes(source) : true;

const filterByProtocol = (item: NetworkRepWithSource, protocol: string) =>
  protocol
    ? item.extra.map(src => src.protocol || src.filename).includes(protocol)
    : true;

const getFileOptions = (data: Array<NetworkRepWithSource>) => {
  const fileNames = data
    .flatMap(item => item.extra)
    .map(item => item.filename)
    .filter(Boolean);
  return Array.from(new Set(fileNames)).map((fileName: string) => ({
    value: fileName,
    label: fileName
  }));
};

export const NETWORK_REFERENCES_ID = "sample-network-references";

const NetworkReferencesContainer = () => {
  const { sourceId, relationshipId } = useLocalSampleSummaryParams();

  const [searchValue, setSearchValue] = useState("");
  const [filterClassification, setFilterClassification] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterProtocol, setFilterProtocol] = useState("");

  const {
    data,
    dataReady,
    stats: networkStats,
    loadMoreNetworkReferences
  } = useNetworkReferences();

  const filteredData = useMemo(() => {
    if (!data?.length) return [];
    return [
      (item: NetworkRepWithSource) => filterByValue(item, searchValue),
      (item: NetworkRepWithSource) =>
        filterByClassification(item, filterClassification),
      (item: NetworkRepWithSource) => filterBySource(item, filterSource),
      (item: NetworkRepWithSource) => filterByProtocol(item, filterProtocol)
    ].reduce((acc, filterFn) => acc.filter(filterFn), data);
  }, [data, searchValue, filterClassification, filterSource, filterProtocol]);

  const {
    data: paginatedData,
    currentPageData,
    updatePageData
  } = usePagination(filteredData || []);

  const fileOptions = getFileOptions(data);

  return (
    <div id={NETWORK_REFERENCES_ID}>
      <RenderWhen condition={networkStats.hasMoreData && dataReady}>
        <div className="flex flex-center mb-25 flex-gap-1">
          <Icon name="warning-i" size={16} style={{ color: "orange" }} />
          <span style={{ fontSize: "16px", lineHeight: "19px" }}>
            Analysis based on the first {formatCount(data?.length ?? 0)} out of
            total {formatCount(networkStats.total)} network references.{" "}
            <span
              onClick={() => loadMoreNetworkReferences()}
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Load More
            </span>
          </span>
        </div>
      </RenderWhen>
      <RenderWhen condition={!dataReady && data?.length > 0}>
        <div className={"flex align-center mb-25"} style={{ gap: "10px" }}>
          <Spinner />
          Loading network references...
        </div>
      </RenderWhen>
      <div className="flex " style={{ gap: "5px" }}>
        <Form.Control
          style={{
            maxWidth: "300px",
            borderRadius: "3px",
            minHeight: "38px",
            fontSize: "1rem"
          }}
          type="text"
          placeholder="Filter by Value"
          name={"fileName"}
          value={searchValue}
          onChange={event => {
            setSearchValue(event.target.value);
            updatePageData({ newPage: 1 });
          }}
          data-testid={`network_refs_search_input`}
        />

        <Select<OptionType>
          options={Object.values(CLASSIFICATION_KEYWORDS).map(item => ({
            value: item,
            label: capitalize(item),
            icon: <ClassificationIcon classification={item} />
          }))}
          testIdKey={"network_refs_classification"}
          isClearable
          placeholder={
            <div style={{ display: "flex", alignItems: "center" }}>
              <ClassificationIcon />
              <span>Classification</span>
            </div>
          }
          onChange={option => {
            setFilterClassification(option?.value || "");
            updatePageData({ newPage: 1 });
          }}
          components={{
            SingleValue: customSingleValue,
            Option: customOption,
            Input: CustomInput,
            DropdownIndicator: CustomDropdownIndicator,
            MultiValueContainer: CustomMultiValueContainer,
            NoOptionsMessage: CustomNoOptionsMessage
          }}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            container: selectContainerStyle
          }}
        />
        <Select<MenuItem>
          options={Object.values(ANALYSIS).map(item => ({
            value: item,
            label: item
          }))}
          testIdKey={"network_refs_source"}
          isClearable
          placeholder="Source"
          onChange={option => {
            setFilterSource(option?.value || "");
            updatePageData({ newPage: 1 });
          }}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999, flex: 1 }),
            container: selectContainerStyle
          }}
          components={{
            Input: CustomInput,
            Option: CustomOption,
            DropdownIndicator: CustomDropdownIndicator,
            MultiValueContainer: CustomMultiValueContainer,
            NoOptionsMessage: CustomNoOptionsMessage
          }}
        />
        <Select<MenuItem>
          options={fileOptions}
          isClearable
          testIdKey={"network_refs_file"}
          placeholder="File"
          onChange={option => {
            setFilterProtocol(option?.value || "");
            updatePageData({ newPage: 1 });
          }}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: 9999,
              flex: 1
            }),
            container: selectContainerStyle
          }}
          components={{
            Input: CustomInput,
            Option: CustomOption,
            DropdownIndicator: CustomDropdownIndicator,
            MultiValueContainer: CustomMultiValueContainer,
            NoOptionsMessage: CustomNoOptionsMessage
          }}
        />
      </div>
      <NetworkReferencesGrid
        data={paginatedData}
        sourceId={sourceId}
        relationshipId={relationshipId}
        hidePaging={false}
        page={currentPageData.page}
        pageSize={currentPageData.pageSize}
        totalResults={currentPageData.totalResults}
        numPages={currentPageData.numPages}
        isLoading={!dataReady && data?.length === 0}
        onPageChange={(newPage: number, newPageSize: number) =>
          updatePageData({ newPage, newPageSize })
        }
      />
    </div>
  );
};

export default memo(NetworkReferencesContainer);
